import {
  ShieldIcon,
  EyeIcon,
  EyeOffIcon,
  UserIcon,
  Building2Icon,
  MailIcon,
  MapPinIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ZapIcon,
  LayoutDashboardIcon,
  ShieldCheckIcon,
} from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useAuthStore, usePortalStore } from '@nebula/shared/store'
import { normalizeDocument } from '@nebula/shared/utils'

interface RegisterForm {
  // Dados pessoais
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  phone: string

  // Dados da empresa
  companyName: string
  companyDocument: string // CNPJ
  companySize: string
  industry: string

  // Endereço
  address: string
  city: string
  state: string
  zipCode: string
  country: string

  // Termos
  acceptTerms: boolean
  acceptMarketing: boolean
}

const STEPS = [
  { id: 1, label: 'Dados', icon: UserIcon },
  { id: 2, label: 'Empresa', icon: Building2Icon },
  { id: 3, label: 'Endereço', icon: MapPinIcon },
  { id: 4, label: 'Termos', icon: ShieldIcon },
] as const

const BENEFITS = [
  { icon: ZapIcon, text: 'Provisione VMs em minutos' },
  { icon: LayoutDashboardIcon, text: 'Rede, storage e backup num só painel' },
  { icon: ShieldCheckIcon, text: 'Segurança e suporte quando precisar' },
] as const

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

const formatPhone = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 11)
  if (!digits) return ''
  if (digits.length <= 2) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`
}

// O CNPJ é alfanumérico desde julho/2026 (letras na raiz e na ordem, DV numérico): a máscara
// aceita `[0-9A-Z]`, não só dígitos — senão a empresa nova não consegue se cadastrar. A régua
// é a do shared (`normalizeDocument`), a mesma do modal da revenda.
const formatCnpj = (value: string): string => {
  const digits = normalizeDocument(value).slice(0, 14)
  if (!digits) return ''
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

const formatCep = (value: string): string => {
  const digits = onlyDigits(value).slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

const RegisterPage = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState('')
  const { register } = useAuthStore()
  const { theme, isPortalDomain } = usePortalStore()

  const [formData, setFormData] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    companyName: '',
    companyDocument: '',
    companySize: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Brasil',
    acceptTerms: false,
    acceptMarketing: false,
  })

  const themed = isPortalDomain && theme
  const primaryColor = themed ? theme.primaryColor : '#9333ea'
  const secondaryColor = themed ? theme.secondaryColor : '#6d28d9'
  const portalName = themed ? theme.portalName : 'Nebvla Cloud'
  const portalTagline =
    themed && theme.portalDescription
      ? theme.portalDescription
      : 'Sua infraestrutura em nuvem, simples e sob seu controle.'
  const privacyPolicyUrl = themed ? theme.privacyPolicyUrl : null
  const termsOfServiceUrl = themed ? theme.termsOfServiceUrl : null
  const registrationEnabled =
    !isPortalDomain || !theme || theme.featuresEnabled.userRegistration !== false

  const inputClassName =
    'w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder-gray-400 transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[color:var(--portal-primary,#9333ea)]'
  const selectClassName =
    'w-full rounded-lg border border-white/30 bg-white/90 px-4 py-3 text-gray-900 transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[color:var(--portal-primary,#9333ea)]'

  // Fundo temático da página (mesmo padrão do LoginPage): imagem da revenda ou
  // brilho radial na cor secundária, com base escura de fallback.
  const pageBackgroundStyle = themed
    ? theme.backgroundImageUrl
      ? {
          backgroundColor: '#030712',
          backgroundImage: `url(${theme.backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {
          backgroundColor: '#030712',
          backgroundImage: `radial-gradient(ellipse at 50% 40%, ${secondaryColor}25 0%, transparent 60%)`,
        }
    : undefined

  if (!registrationEnabled) {
    return <Navigate to="/login" replace />
  }

  const updateFormData = (field: keyof RegisterForm, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(
          formData.firstName.trim().length >= 2 &&
          formData.lastName.trim().length >= 2 &&
          EMAIL_REGEX.test(formData.email) &&
          formData.email.length >= 6 &&
          formData.password.length >= 8 &&
          formData.confirmPassword.length >= 8 &&
          onlyDigits(formData.phone).length >= 10
        )
      case 2:
        return !!(
          formData.companyName.trim().length >= 2 &&
          normalizeDocument(formData.companyDocument).length === 14 &&
          formData.companySize &&
          formData.industry
        )
      case 3:
        return !!(
          formData.address.trim().length >= 5 &&
          formData.city.trim().length >= 2 &&
          formData.state &&
          onlyDigits(formData.zipCode).length === 8 &&
          formData.country.trim().length >= 2
        )
      case 4:
        return formData.acceptTerms
      default:
        return false
    }
  }

  const handleNext = () => {
    if (currentStep === 1) {
      // Validar senhas
      if (formData.password !== formData.confirmPassword) {
        toast.error('As senhas não coincidem')
        return
      }
      if (formData.password.length < 8) {
        toast.error('A senha deve ter pelo menos 8 caracteres')
        return
      }
      if (!EMAIL_REGEX.test(formData.email)) {
        toast.error('Informe um email válido')
        return
      }
    }

    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4))
    } else {
      toast.error('Preencha todos os campos obrigatórios')
    }
  }

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Enter num input do passo 1-3 disparava submit com o formulário incompleto
    if (currentStep < 4) {
      handleNext()
      return
    }

    if (!validateStep(4)) {
      toast.error('Você deve aceitar os termos de uso')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem')
      return
    }
    if (formData.password.length < 8) {
      toast.error('A senha deve ter pelo menos 8 caracteres')
      return
    }

    setLoading(true)

    try {
      const registerData = {
        user: {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          phone: onlyDigits(formData.phone),
        },
        organization: {
          name: formData.companyName.trim(),
          document: normalizeDocument(formData.companyDocument),
          size: formData.companySize,
          industry: formData.industry,
          type: 'end_customer',
        },
        address: {
          street: formData.address.trim(),
          city: formData.city.trim(),
          state: formData.state,
          zip_code: onlyDigits(formData.zipCode),
          country: formData.country.trim(),
        },
        preferences: {
          marketing_emails: formData.acceptMarketing,
        },
        frontend_url: window.location.origin,
      }

      const response = (await register(registerData)) as any
      toast.success(response?.message || 'Cadastro realizado com sucesso!')

      // Show success screen instead of redirecting
      setRegisteredEmail(formData.email)
      setRegistrationSuccess(true)
    } catch (error: any) {
      console.error('Erro no registro:', error)
      toast.error(error.response?.data?.detail || 'Erro ao criar conta')
    } finally {
      setLoading(false)
    }
  }

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">Nome *</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => updateFormData('firstName', e.target.value)}
            className={inputClassName}
            placeholder="Seu nome"
            minLength={2}
            maxLength={100}
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">Sobrenome *</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => updateFormData('lastName', e.target.value)}
            className={inputClassName}
            placeholder="Seu sobrenome"
            minLength={2}
            maxLength={100}
            required
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">Email *</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => updateFormData('email', e.target.value)}
          className={inputClassName}
          placeholder="seu@email.com"
          minLength={6}
          maxLength={254}
          required
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">Telefone *</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => updateFormData('phone', formatPhone(e.target.value))}
          className={inputClassName}
          placeholder="(11) 9 9999-9999"
          inputMode="numeric"
          maxLength={16}
          required
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">Senha *</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => updateFormData('password', e.target.value)}
            className={`${inputClassName} pr-12`}
            placeholder="Mínimo 8 caracteres"
            minLength={8}
            maxLength={128}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-white"
          >
            {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">Confirmar Senha *</label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => updateFormData('confirmPassword', e.target.value)}
            className={`${inputClassName} pr-12`}
            placeholder="Confirme sua senha"
            minLength={8}
            maxLength={128}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-white"
          >
            {showConfirmPassword ? (
              <EyeOffIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">Nome da Empresa *</label>
        <input
          type="text"
          value={formData.companyName}
          onChange={(e) => updateFormData('companyName', e.target.value)}
          className={inputClassName}
          placeholder="Sua Empresa Ltda"
          minLength={2}
          maxLength={200}
          required
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">CNPJ *</label>
        <input
          type="text"
          value={formData.companyDocument}
          onChange={(e) => updateFormData('companyDocument', formatCnpj(e.target.value))}
          className={inputClassName}
          placeholder="00.000.000/0001-00"
          autoCapitalize="characters"
          maxLength={18}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">Porte da Empresa *</label>
          <select
            value={formData.companySize}
            onChange={(e) => updateFormData('companySize', e.target.value)}
            className={selectClassName}
            required
          >
            <option value="">Selecione</option>
            <option value="micro">Microempresa (até 9 funcionários)</option>
            <option value="small">Pequena (10-49 funcionários)</option>
            <option value="medium">Média (50-249 funcionários)</option>
            <option value="large">Grande (250+ funcionários)</option>
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">Setor de Atuação *</label>
          <select
            value={formData.industry}
            onChange={(e) => updateFormData('industry', e.target.value)}
            className={selectClassName}
            required
          >
            <option value="">Selecione</option>
            <option value="technology">Tecnologia</option>
            <option value="finance">Financeiro</option>
            <option value="healthcare">Saúde</option>
            <option value="education">Educação</option>
            <option value="retail">Varejo</option>
            <option value="manufacturing">Indústria</option>
            <option value="services">Serviços</option>
            <option value="other">Outros</option>
          </select>
        </div>
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-200">Endereço *</label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => updateFormData('address', e.target.value)}
          className={inputClassName}
          placeholder="Rua, número, complemento"
          minLength={5}
          maxLength={255}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">Cidade *</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => updateFormData('city', e.target.value)}
            className={inputClassName}
            placeholder="Sua cidade"
            minLength={2}
            maxLength={100}
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">Estado *</label>
          <select
            value={formData.state}
            onChange={(e) => updateFormData('state', e.target.value)}
            className={selectClassName}
            required
          >
            <option value="">Selecione</option>
            <option value="SP">São Paulo</option>
            <option value="RJ">Rio de Janeiro</option>
            <option value="MG">Minas Gerais</option>
            <option value="RS">Rio Grande do Sul</option>
            <option value="PR">Paraná</option>
            <option value="SC">Santa Catarina</option>
            <option value="GO">Goiás</option>
            <option value="DF">Distrito Federal</option>
            <option value="BA">Bahia</option>
            <option value="PE">Pernambuco</option>
            <option value="CE">Ceará</option>
            <option value="ES">Espírito Santo</option>
            <option value="MT">Mato Grosso</option>
            <option value="MS">Mato Grosso do Sul</option>
            <option value="PA">Pará</option>
            <option value="AM">Amazonas</option>
            <option value="RO">Rondônia</option>
            <option value="AC">Acre</option>
            <option value="TO">Tocantins</option>
            <option value="MA">Maranhão</option>
            <option value="PI">Piauí</option>
            <option value="AL">Alagoas</option>
            <option value="SE">Sergipe</option>
            <option value="PB">Paraíba</option>
            <option value="RN">Rio Grande do Norte</option>
            <option value="RR">Roraima</option>
            <option value="AP">Amapá</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">CEP *</label>
          <input
            type="text"
            value={formData.zipCode}
            onChange={(e) => updateFormData('zipCode', formatCep(e.target.value))}
            className={inputClassName}
            placeholder="00000-000"
            inputMode="numeric"
            maxLength={9}
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-200">País *</label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => updateFormData('country', e.target.value)}
            className={inputClassName}
            placeholder="Brasil"
            minLength={2}
            maxLength={100}
            required
          />
        </div>
      </div>
    </div>
  )

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <h3 className="mb-2 font-semibold text-white">Resumo do Cadastro</h3>
        <div className="space-y-1 text-sm text-gray-300">
          <p>
            <strong>Nome:</strong> {formData.firstName} {formData.lastName}
          </p>
          <p>
            <strong>Email:</strong> {formData.email}
          </p>
          <p>
            <strong>Empresa:</strong> {formData.companyName}
          </p>
          <p>
            <strong>Cidade:</strong> {formData.city}, {formData.state}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <label className="flex items-start space-x-3">
          <input
            type="checkbox"
            checked={formData.acceptTerms}
            onChange={(e) => updateFormData('acceptTerms', e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10"
            style={{ accentColor: primaryColor }}
            required
          />
          <span className="text-sm text-gray-200">
            Eu aceito os{' '}
            {termsOfServiceUrl ? (
              <a
                href={termsOfServiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
                style={{ color: primaryColor }}
              >
                Termos de Uso
              </a>
            ) : (
              <span className="underline" style={{ color: primaryColor }}>
                Termos de Uso
              </span>
            )}{' '}
            e a{' '}
            {privacyPolicyUrl ? (
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
                style={{ color: primaryColor }}
              >
                Política de Privacidade
              </a>
            ) : (
              <span className="underline" style={{ color: primaryColor }}>
                Política de Privacidade
              </span>
            )}{' '}
            da {portalName} *
          </span>
        </label>

        <label className="flex items-start space-x-3">
          <input
            type="checkbox"
            checked={formData.acceptMarketing}
            onChange={(e) => updateFormData('acceptMarketing', e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10"
            style={{ accentColor: primaryColor }}
          />
          <span className="text-sm text-gray-200">
            Aceito receber emails com novidades, promoções e atualizações sobre produtos
          </span>
        </label>
      </div>

      <div
        className="rounded-lg border p-4"
        style={{ backgroundColor: `${primaryColor}1A`, borderColor: `${primaryColor}4D` }}
      >
        <div className="flex items-start gap-3">
          <MailIcon className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: primaryColor }} />
          <p className="text-sm text-gray-200">
            <strong className="text-white">Verificação de Email:</strong> após o cadastro, você
            receberá um email de confirmação. Clique no link para ativar sua conta e começar a usar
            o portal {portalName}.
          </p>
        </div>
      </div>
    </div>
  )

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Dados Pessoais'
      case 2:
        return 'Dados da Empresa'
      case 3:
        return 'Endereço'
      case 4:
        return 'Termos e Confirmação'
      default:
        return ''
    }
  }

  // Marca da revenda (logo ou nome + ícone), reutilizada no painel lateral e no
  // cabeçalho compacto do mobile.
  const renderBrandMark = (size: 'lg' | 'sm') =>
    themed && theme.logoUrl ? (
      <img
        src={theme.logoUrl}
        alt={portalName}
        className={
          size === 'lg'
            ? 'max-h-16 w-auto max-w-[240px] object-contain'
            : 'max-h-12 w-auto max-w-[200px] object-contain'
        }
      />
    ) : (
      <div className="flex items-center gap-3">
        <div className="rounded-full p-3" style={{ backgroundColor: `${primaryColor}33` }}>
          <ShieldIcon
            className={size === 'lg' ? 'h-8 w-8' : 'h-6 w-6'}
            style={{ color: primaryColor }}
          />
        </div>
        <span className={`font-bold text-white ${size === 'lg' ? 'text-2xl' : 'text-lg'}`}>
          {portalName}
        </span>
      </div>
    )

  // Show success screen after registration
  if (registrationSuccess) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center p-4 ${themed ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
        style={pageBackgroundStyle}
      >
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
            <div className="mb-8 flex justify-center">
              <div className="rounded-full bg-green-600/20 p-4">
                <CheckCircleIcon className="h-12 w-12 text-green-500" />
              </div>
            </div>

            <h1 className="mb-4 text-center text-2xl font-bold text-white">Cadastro Realizado!</h1>

            <p className="mb-4 text-center text-gray-300">Enviamos um email de verificação para:</p>

            <p className="mb-6 text-center text-lg font-medium" style={{ color: primaryColor }}>
              {registeredEmail}
            </p>

            <div
              className="mb-6 rounded-lg border p-4"
              style={{ backgroundColor: `${primaryColor}1A`, borderColor: `${primaryColor}4D` }}
            >
              <div className="flex items-start gap-3">
                <MailIcon
                  className="mt-0.5 h-5 w-5 flex-shrink-0"
                  style={{ color: primaryColor }}
                />
                <div className="text-sm text-gray-300">
                  <p className="mb-1 font-medium text-white">Verifique seu email</p>
                  <p>Clique no link enviado para ativar sua conta. O link expira em 48 horas.</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Link
                to="/login"
                className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                Ir para o Login
              </Link>

              <p className="text-center text-xs text-gray-400">
                Não recebeu o email? Verifique a pasta de spam ou{' '}
                <Link to="/verify-email" className="underline" style={{ color: primaryColor }}>
                  solicite um novo link
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex min-h-screen items-center justify-center p-4 py-8 ${themed ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
      style={pageBackgroundStyle}
    >
      <div className="w-full max-w-5xl">
        <div className="grid overflow-hidden rounded-2xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-lg lg:grid-cols-5">
          {/* Painel de marca (desktop) — base escura + brilhos da cor da revenda
              (contraste garantido mesmo com cor primária clara). */}
          <div
            className="relative hidden flex-col justify-between p-10 lg:col-span-2 lg:flex"
            style={{
              backgroundColor: '#0b1020',
              backgroundImage: `radial-gradient(circle at 15% 15%, ${primaryColor}40, transparent 55%), radial-gradient(circle at 85% 85%, ${secondaryColor}33, transparent 55%)`,
            }}
          >
            <div>{renderBrandMark('lg')}</div>

            <div className="space-y-8">
              <div>
                <p className="text-3xl leading-tight font-bold text-white">{portalTagline}</p>
                <p className="mt-3 text-gray-300">
                  Crie sua conta e comece a usar a nuvem em poucos minutos.
                </p>
              </div>

              <ul className="space-y-4">
                {BENEFITS.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-3 text-gray-200">
                    <span
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${primaryColor}26`, color: primaryColor }}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-sm">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="flex items-center gap-2 text-xs text-gray-400">
              <ShieldCheckIcon className="h-4 w-4" />
              Seus dados são protegidos e nunca compartilhados.
            </p>
          </div>

          {/* Painel do formulário */}
          <div className="p-6 sm:p-10 lg:col-span-3">
            {/* Cabeçalho compacto da marca (só no mobile — no desktop a marca fica no painel lateral) */}
            <div className="mb-6 flex justify-center lg:hidden">{renderBrandMark('sm')}</div>

            <div className="mb-6">
              <h1 className="text-2xl font-bold text-white">Criar nova conta</h1>
              <p className="mt-1 text-sm text-gray-300">
                Passo {currentStep} de 4 · {getStepTitle()}
              </p>
            </div>

            {/* Stepper */}
            <div className="mb-8 flex items-center">
              {STEPS.map((step, idx) => {
                const isDone = step.id < currentStep
                const isActive = step.id === currentStep
                return (
                  <div
                    key={step.id}
                    className={`flex items-center ${idx < STEPS.length - 1 ? 'flex-1' : ''}`}
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                          isDone || isActive ? 'text-white' : 'bg-white/10 text-gray-400'
                        }`}
                        style={isDone || isActive ? { backgroundColor: primaryColor } : undefined}
                      >
                        {isDone ? <CheckIcon className="h-5 w-5" /> : step.id}
                      </div>
                      <span
                        className={`text-xs whitespace-nowrap ${isActive ? 'font-medium text-white' : 'text-gray-400'}`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className="mx-2 -mt-5 h-0.5 flex-1 overflow-hidden rounded-full bg-white/10">
                        <div
                          className="h-full transition-all"
                          style={{ width: isDone ? '100%' : '0%', backgroundColor: primaryColor }}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Conteúdo do formulário */}
            <form onSubmit={handleSubmit}>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}

              {/* Botões de navegação */}
              <div className="mt-8 flex items-center justify-between gap-4">
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      className="flex items-center px-4 py-3 text-gray-300 transition-colors hover:text-white"
                    >
                      <ArrowLeftIcon className="mr-2 h-4 w-4" />
                      Anterior
                    </button>
                  )}
                </div>

                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!validateStep(currentStep)}
                    className="rounded-lg px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Próximo
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading || !validateStep(4)}
                    className="flex items-center rounded-lg bg-green-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                        Criando Conta...
                      </>
                    ) : (
                      'Criar Conta'
                    )}
                  </button>
                )}
              </div>

              <p className="mt-6 text-center text-sm text-gray-400">
                Já tem conta?{' '}
                <Link
                  to="/login"
                  className="transition-colors hover:opacity-80"
                  style={{ color: primaryColor }}
                >
                  Faça login
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
